import React, { useState } from 'react';
import axios from 'axios';
import {
    Chip, TextField, Button, CircularProgress, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Box, makeStyles, Typography, Paper,
    Tooltip, Grid, Divider, Card, CardContent, useTheme, useMediaQuery, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, withStyles
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import InfoIcon from '@material-ui/icons/Info';
import CalculateIcon from '@material-ui/icons/FunctionsOutlined';
import AccountBalanceWalletIcon from '@material-ui/icons/AccountBalanceWallet';
import SearchIcon from '@material-ui/icons/Search';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import GroupIcon from '@material-ui/icons/Group';
import {
    getLocalTimeString,
    isQuestOver,
    isQuestPaid,
    isAutoStreamQuest
} from '../../../utils/functions';
import './QuestDataReset.scss';
import {getCurCurrency} from "../../../utils/util";

const useStyles = makeStyles((theme) => ({
    container: {
        backgroundColor: '#1e1e28',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(4),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(3),
        boxShadow: '0 3px 10px rgba(0, 0, 0, 0.2)',
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
            transform: 'translateY(-2px)',
        },
    },
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        maxWidth: 600,
        gap: theme.spacing(3.5),
        margin: '0 auto',
    },
    title: {
        color: '#fff',
        marginBottom: theme.spacing(2),
        fontSize: 22,
        fontWeight: 500,
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1.5),
            color: theme.palette.primary.main,
        },
    },
    inputField: {
        flex: '1 1 auto', // 文本框占据剩余空间
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#252530',
            '& fieldset': {
                borderColor: '#444',
                transition: 'border-color 0.3s',
            },
            '&:hover fieldset': {
                borderColor: '#666',
            },
            '&.Mui-focused fieldset': {
                borderColor: theme.palette.primary.main,
                borderWidth: 2,
            },
        },
        '& .MuiOutlinedInput-input': {
            color: '#fff',
            padding: '16px',
        },
        '& .MuiInputLabel-outlined': {
            color: '#888',
            '&.Mui-focused': {
                color: theme.palette.primary.main,
            },
        },
    },
    searchButton: {
        height: 56,
        marginLeft: theme.spacing(1.5),
        backgroundColor: theme.palette.primary.main,
        color: 'white',
        padding: '10px 20px',
        minWidth: 150, // 增加按钮宽度
        width: 'auto',
        flex: '0 0 150px', // 固定宽度组件
        boxShadow: '0 2px 8px rgba(33, 150, 243, 0.3)',
        transition: 'all 0.3s',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 4px 12px rgba(33, 150, 243, 0.5)',
        },
        '&.Mui-disabled': {
            backgroundColor: 'rgba(33, 150, 243, 0.3)',
        },
    },
    searchContainer: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        flexWrap: 'nowrap', // 确保不换行
    },
    updateButton: {
        marginTop: theme.spacing(2),
        backgroundColor: '#2196f3',
        color: 'white',
        padding: '12px 24px',
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0 2px 10px rgba(33, 150, 243, 0.3)',
        '&:hover': {
            backgroundColor: '#1976d2',
            boxShadow: '0 4px 15px rgba(33, 150, 243, 0.4)',
        },
        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
            backgroundColor: 'rgba(33, 150, 243, 0.5)',
        },
    },
    calculateButton: {
        marginTop: theme.spacing(2),
        backgroundColor: '#ff9800',
        color: 'white',
        padding: '12px 24px',
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0 2px 10px rgba(255, 152, 0, 0.3)',
        '&:hover': {
            backgroundColor: '#f57c00',
            boxShadow: '0 4px 15px rgba(255, 152, 0, 0.4)',
        },
        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
            backgroundColor: 'rgba(255, 152, 0, 0.5)',
        },
    },
    settlementButton: {
        marginTop: theme.spacing(2),
        backgroundColor: '#4caf50',
        color: 'white',
        padding: '12px 24px',
        borderRadius: theme.shape.borderRadius,
        boxShadow: '0 2px 10px rgba(76, 175, 80, 0.3)',
        '&:hover': {
            backgroundColor: '#388e3c',
            boxShadow: '0 4px 15px rgba(76, 175, 80, 0.4)',
        },
        '&.Mui-disabled': {
            color: 'rgba(255, 255, 255, 0.3)',
            backgroundColor: 'rgba(76, 175, 80, 0.5)',
        },
    },
    errorMessage: {
        color: '#ff5a5f',
        margin: theme.spacing(2, 0),
        padding: theme.spacing(1.5, 2),
        backgroundColor: 'rgba(255, 90, 95, 0.1)',
        borderRadius: theme.shape.borderRadius,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        border: '1px solid rgba(255, 90, 95, 0.2)',
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
        },
    },
    successMessage: {
        color: '#4caf50',
        margin: theme.spacing(2, 0),
        padding: theme.spacing(1.5, 2),
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        borderRadius: theme.shape.borderRadius,
        fontSize: '14px',
        display: 'flex',
        alignItems: 'flex-start', // 改为flex-start以便支持多行内容
        border: '1px solid rgba(76, 175, 80, 0.2)',
        '& .MuiSvgIcon-root': {
            marginRight: theme.spacing(1),
            marginTop: '3px', // 图标稍微下移
        },
        '& ul': {
            margin: theme.spacing(0.5, 0, 0.5, 2),
            paddingLeft: theme.spacing(1),
        },
        '& li': {
            margin: theme.spacing(0.5, 0),
        }
    },
    dialogTitle: {
        backgroundColor: '#252530',
        color: '#fff',
    },
    dialogContent: {
        backgroundColor: '#1e1e28',
        color: '#ddd',
        padding: theme.spacing(3),
    },
    dialogActions: {
        backgroundColor: '#1e1e28',
        padding: theme.spacing(2, 3),
    },
    cancelButton: {
        color: '#aaa',
        fontWeight: 500,
        '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
        },
    },
    confirmButton: {
        backgroundColor: '#d32f2f',
        color: 'white',
        '&:hover': {
            backgroundColor: '#b71c1c',
        },
    },
    divider: {
        backgroundColor: '#333',
        margin: theme.spacing(5, 0, 3),
    },
    description: {
        color: '#aaa',
        fontSize: '14px',
        marginBottom: theme.spacing(3),
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1),
            fontSize: '1.2rem',
            color: '#888',
        },
    },
    iconWithText: {
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1),
        }
    },
    sectionIcon: {
        marginRight: theme.spacing(1.5),
        color: theme.palette.primary.main,
        fontSize: '1.8rem',
    },
    '@keyframes fadeIn': {
        '0%': {
            opacity: 0,
            transform: 'translateY(10px)'
        },
        '100%': {
            opacity: 1,
            transform: 'translateY(0)'
        }
    },
    questDetails: {
        backgroundColor: '#252530',
        borderRadius: theme.shape.borderRadius,
        padding: theme.spacing(2.5),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
        width: '100%',
        animation: '$fadeIn 0.4s ease-out',
    },
    questDetailItem: {
        display: 'flex',
        marginBottom: theme.spacing(1),
        borderBottom: '1px solid #333',
        paddingBottom: theme.spacing(1),
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    questDetailLabel: {
        color: '#9b9b9b',
        fontWeight: 500,
        width: 150,
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginRight: theme.spacing(1),
            fontSize: '0.9rem',
        },
    },
    questDetailValue: {
        color: '#fff',
        fontWeight: 400,
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
    },
    statusChip: {
        padding: '4px 14px',
        borderRadius: 16,
        display: 'inline-block',
        fontWeight: 500,
        fontSize: 14,
        marginRight: theme.spacing(1),
    },
    activeStatus: {
        backgroundColor: 'rgba(76, 175, 80, 0.2)',
        color: '#4caf50',
    },
    inactiveStatus: {
        backgroundColor: 'rgba(244, 67, 54, 0.2)',
        color: '#f44336',
    },
    warningStatus: {
        backgroundColor: 'rgba(255, 152, 0, 0.2)',
        color: '#ff9800',
    },
    paidStatus: {
        backgroundColor: 'rgba(33, 150, 243, 0.2)',
        color: '#2196f3',
    },
    warningIcon: {
        color: '#ff9800',
        marginLeft: theme.spacing(1),
    },
    infoIcon: {
        color: '#2196f3',
        marginRight: theme.spacing(1),
        fontSize: '1.2rem',
    },
    warningNote: {
        backgroundColor: 'rgba(33, 150, 243, 0.1)',
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.5),
        display: 'flex',
        alignItems: 'center',
    },
    configItem: {
        marginRight: theme.spacing(2),
        backgroundColor: 'rgba(0, 0, 0, 0.2)',
        padding: theme.spacing(0.5, 1),
        borderRadius: theme.spacing(0.5),
    }, pendingStatus: {
        backgroundColor: 'rgba(255, 193, 7, 0.2)',
        color: '#ffc107',
    },

    // 确保表格样式也存在
    tableContainer: {
        backgroundColor: '#252530',
        borderRadius: 8,
        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
        overflow: 'auto',
        position: 'relative',
        '&::-webkit-scrollbar': {
            height: 8,
            backgroundColor: '#1e1e28',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#444',
            borderRadius: 4,
        },
    },
    tableCell: {
        color: '#fff',
        borderBottomColor: '#333',
        padding: '12px 16px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    tableRow: {
        transition: 'background-color 0.2s',
        '&:nth-of-type(odd)': {
            backgroundColor: 'rgba(255, 255, 255, 0.03)',
        },
        '&:hover': {
            backgroundColor: 'rgba(33, 150, 243, 0.05)',
        },
    },
    // TableHeaderCell 组件需要通过 withStyles 定义
}));

// 定义表头单元格样式
const TableHeaderCell = withStyles(() => ({
    head: {
        color: '#9b9b9b',
        fontWeight: '500',
        letterSpacing: '1.2px',
        fontSize: '14px',
        borderBottom: '2px solid #333',
        padding: '12px 16px',
        backgroundColor: '#1e1e28',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
}))(TableCell);

function QuestDataReset(props) {
    // 在组件顶部添加状态
    const [settlementReason, setSettlementReason] = useState('');
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // 在组件顶部添加状态
    const [settlementUserQuests, setSettlementUserQuests] = useState([]);
    const [recalculateUserQuests, setRecalculateUserQuests] = useState([]);
    // 通用对话框状态
    const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [confirmTitle, setConfirmTitle] = useState('');
    const [confirmContent, setConfirmContent] = useState('');
    const [confirmActionType, setConfirmActionType] = useState(''); // 新增状态用于标识操作类型

    // 已移除排行榜相关状态

    // 重新结算状态
    const [recalculateQuestId, setRecalculateQuestId] = useState('');
    const [isRecalculating, setIsRecalculating] = useState(false);
    const [recalculateError, setRecalculateError] = useState('');
    const [recalculateSuccess, setRecalculateSuccess] = useState('');
    const [recalculateQuestInfo, setRecalculateQuestInfo] = useState(null);
    const [isLoadingRecalculateQuest, setIsLoadingRecalculateQuest] = useState(false);

    // 结算入账状态
    const [settlementQuestId, setSettlementQuestId] = useState('');
    const [isSettling, setIsSettling] = useState(false);
    const [settlementError, setSettlementError] = useState('');
    const [settlementSuccess, setSettlementSuccess] = useState('');
    const [settlementQuestInfo, setSettlementQuestInfo] = useState(null);
    const [isLoadingSettlementQuest, setIsLoadingSettlementQuest] = useState(false);

    const [endTaskReason, setEndTaskReason] = useState('');

    const handleOpenConfirmDialog = (actionType) => {
        let id = '';
        let title = '';
        let content = '';
        let action = null;

        switch (actionType) {
            case 'recalculate':
                id = recalculateQuestId;
                title = '确认强制结束任务并重新结算';
                content = `您确定要强制结束任务ID为 <strong style="color: #ff5a5f">${recalculateQuestId}</strong> 并重新结算任务奖励吗？<br/><br/>
                        <span style="color: #ff9800; font-weight: 500">此操作将会：</span>
                        <ul style="color: #ff9800;">
                            <li>将任务标记为结束状态</li>
                            <li>生成最终排行榜数据</li>
                            <li>触发奖励计算流程</li>
                        </ul>
                        <p style="color:#ff5a5f;font-weight:bold">警告：此操作不可撤销！</p>
                        <p style="color:#fff;margin-top:10px;">请填写任务强制结束的原因：</p>`;
                // 重置任务结束原因
                setEndTaskReason('');
                action = handleRecalculateRewards;
                break;
            case 'settlement':
                id = settlementQuestId;
                title = '确认自动结算入账';
                content = `您确定要将任务ID为 <strong style="color: #ff5a5f">${settlementQuestId}</strong> 的奖励自动结算到用户账户吗？<br/><br/>
                        <span style="color: #ff9800; font-weight: 500">此操作将会：</span>
                        <ul style="color: #ff9800;">
                            <li>将任务标记为已结算状态</li>
                            <li>将所有用户奖励自动入账到他们的钱包</li>
                            <li>更新所有用户任务状态为已支付</li>
                        </ul>
                        <p style="color:#ff5a5f;font-weight:bold">警告：此操作执行后将直接影响用户余额，请谨慎操作！</p>
                        <p style="color:#fff;margin-top:10px;">请填写结算原因（可选）：</p>`;
                // 重置结算原因
                setSettlementReason('');
                action = handleSettleRewards;
                break;
            default:
                return;
        }

        if (!id) {
            // 根据操作类型设置错误消息
            switch (actionType) {
                case 'recalculate':
                    setRecalculateError('请输入有效的任务ID');
                    break;
                case 'settlement':
                    setSettlementError('请输入有效的任务ID');
                    break;
                default:
                    break;
            }
            return;
        }

        // 清除相应的错误消息
        switch (actionType) {
            case 'recalculate':
                setRecalculateError('');
                break;
            case 'settlement':
                setSettlementError('');
                break;
            default:
                break;
        }

        setConfirmTitle(title);
        setConfirmContent(content);
        setConfirmAction(action);
        setOpenConfirmDialog(true);
        setConfirmActionType(actionType); // 保存操作类型
    };

    const handleCloseConfirmDialog = () => {
        setOpenConfirmDialog(false);
    };

    // 通用查询任务详情函数
    const fetchQuestInfo = async (questId, setQuestInfo, setIsLoading, setError, setUserQuests) => {
        console.log(confirmAction);
        if (!questId) {
            setError('请输入有效的任务ID');
            return;
        }

        setIsLoading(true);
        setError('');
        setQuestInfo(null);

        try {
            const { token } = props.auth;

            // 创建请求配置
            const config = {
                headers: {
                    Authorization: `JWT ${token}`,
                    'Content-Type': 'application/json',
                }
            };

            // 发送API请求获取任务详情
            const response = await axios.get(`/api/v1/admin/quest/${questId}/tiered`, config);

            // 检查响应中是否包含数据
            if (response.data && response.data.code === "QUEST_TIERED_ACTIVITY") {
                // 构建增强的任务信息对象，包含排行榜和用户任务等附加数据
                const enhancedQuestInfo = {
                    ...response.data.quest,  // 基本任务信息

                    // 添加额外有用数据
                    leaderboardData: response.data.leaderboard || [],
                    userQuestsData: response.data.userQuests || [],
                    participantsCount: (response.data.userQuests || []).length,

                    // 统计数据
                    keysAvailable: response.data.keysAvailables || 0,
                    activeGamesCount: (response.data.activeGames || []).length,
                    activeQuestsCount: (response.data.activeQuests || []).length,

                    // 元数据
                    dataFetchTime: new Date().toISOString(),
                    responseCode: response.data.code,
                    responseMessage: response.data.message
                };

                // 设置增强后的任务信息
                setQuestInfo(enhancedQuestInfo);

                // 如果有设置 userQuests 的状态更新函数，则更新用户任务数据
                if (setUserQuests && response.data.userQuests) {
                    setUserQuests(response.data.userQuests);
                }

                console.log('已成功获取完整任务数据，包含相关附加信息');
            } else {
                throw new Error(response.data?.message || '未找到任务信息或数据格式错误');
            }
        } catch (err) {
            console.error('查询任务错误:', err);
            const errorMessage = err.response?.data?.message || err.message || '查询任务失败，请检查任务ID';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    // 已移除排行榜相关函数

    // 3. 查询重新结算任务
    const handleRecalculateQuestIdChange = (e) => {
        setRecalculateQuestId(e.target.value);
        // 清除之前的任务信息
        setRecalculateQuestInfo(null);
    };

    const fetchRecalculateQuest = () => {
        fetchQuestInfo(
            recalculateQuestId,
            setRecalculateQuestInfo,
            setIsLoadingRecalculateQuest,
            setRecalculateError,
            setRecalculateUserQuests
        );
    };

    // 4. 重新结算任务奖励函数
    // 修改 handleRecalculateRewards 函数，集成任务强制结束逻辑
    const handleRecalculateRewards = async () => {
        // 如果没有填写任务结束原因，则不执行
        if (!endTaskReason.trim()) {
            return;
        }

        setOpenConfirmDialog(false);
        setIsRecalculating(true);
        setRecalculateError('');
        setRecalculateSuccess('');

        try {
            const { token } = props.auth;

            // 创建请求配置
            const config = {
                headers: {
                    Authorization: `JWT ${token}`,
                    'Content-Type': 'application/json',
                }
            };

            // 构建请求体，包含任务ID和结束原因
            const body = {
                questId: recalculateQuestId,
                reason: endTaskReason,
                forceEnd: true
            };

            // 发送API请求强制结束任务
            const response = await axios.post('/api/v1/user/test-update-quest-end', body, config);

            // 1. 首先检查响应状态码
            if (response.status !== 200) {
                throw new Error(`服务器响应错误，状态码: ${response.status}`);
            }

            // 2. 再检查响应内容中的成功标志或特定状态码
            if ((response.data && response.data.success) ||
                (response.data && response.data.code === "QUEST_FORCE_ENDED")) {

                setIsRecalculating(false);
                setRecalculateSuccess(`已成功强制结束任务 ${recalculateQuestId} 并重新计算排行榜`);

                // 清空输入内容和状态
                setEndTaskReason('');
                setRecalculateQuestId('');
                setRecalculateQuestInfo(null);
            } else {
                // 如果状态码是200但没有成功标志也没有特定状态码，抛出错误
                throw new Error(response.data?.message || '强制结束任务返回了意外的响应格式');
            }
        } catch (err) {
            // 增强错误消息展示
            let errorMessage;
            if (err.response) {
                // 服务器返回了错误响应
                errorMessage = err.response.data?.message || `服务器错误 (${err.response.status})`;
            } else if (err.request) {
                // 请求已发送但没有收到响应
                errorMessage = '服务器无响应，请检查网络连接';
            } else {
                // 请求设置时发生错误
                errorMessage = err.message || '强制结束任务失败，请稍后重试';
            }
            setRecalculateError(errorMessage);
            setIsRecalculating(false);
        }
    };

    // 5. 查询结算入账任务
    const handleSettlementQuestIdChange = (e) => {
        setSettlementQuestId(e.target.value);
        // 清除之前的任务信息
        setSettlementQuestInfo(null);
    };

    const fetchSettlementQuest = () => {
        fetchQuestInfo(
            settlementQuestId,
            setSettlementQuestInfo,
            setIsLoadingSettlementQuest,
            setSettlementError,
            setSettlementUserQuests
        );
    };

    // 6. 自动结算入账函数
    // 替换 handleSettleRewards 函数
    const handleSettleRewards = async () => {
        setOpenConfirmDialog(false);
        setIsSettling(true);
        setSettlementError('');
        setSettlementSuccess('');

        try {
            const { token } = props.auth;

            // 创建请求配置
            const config = {
                headers: {
                    Authorization: `JWT ${token}`,
                    'Content-Type': 'application/json',
                }
            };

            // 构建请求体，包含任务ID和结算原因
            const body = {
                questId: settlementQuestId,
                reason: settlementReason || '管理员手动结算'
            };

            // 发送API请求结算任务奖励，使用正确的API路径
            const response = await axios.post('/api/v1/user/test-update-quest-auto-checkout', body, config);

            // 检查响应状态和代码
            if (response.status === 200 && response.data && response.data.code === 'QUEST_MANUAL_SETTLEMENT_COMPLETED') {
                const stats = response.data.statistics || {};
                setIsSettling(false);

                // 使用返回的统计数据构建详细的成功消息
                const successMsg = `
                成功将任务 ${settlementQuestId} 的奖励自动结算到用户账户
                <br/><br/>
                <strong>处理结果:</strong>
                <ul>
                    <li>处理用户: ${stats.processedCount || 0} 人</li>
                    <li>结算成功: ${stats.successCount || 0} 人</li>
                    ${stats.failedCount > 0 ? `<li style="color:#ff9800">结算失败: ${stats.failedCount} 人</li>` : ''}
                    <li>结算总额: ${getCurCurrency(props.auth.user,stats.totalAmount)}</li>
                    <li>处理时间: ${Math.round((stats.executionTime || 0) / 1000)}秒</li>
                </ul>
            `;

                setSettlementSuccess(successMsg);

                // 成功后清空输入框和任务信息
                setSettlementQuestId('');
                setSettlementQuestInfo(null);
                setSettlementUserQuests([]);
            } else {
                // 如果状态码是200但没有正确的成功标志，抛出错误
                throw new Error(response.data?.message || '结算入账返回了意外的响应格式');
            }
        } catch (err) {
            console.error('奖励自动结算入账错误:', err);

            // 根据错误代码提供更具体的错误消息
            let errorMessage = '奖励自动结算入账失败，请稍后重试';

            if (err.response && err.response.data) {
                const errorCode = err.response.data.code;

                switch (errorCode) {
                    case 'E_QUEST_NOT_FOUND':
                        errorMessage = '任务不存在，请检查任务ID';
                        break;
                    case 'E_QUEST_ALREADY_SETTLED':
                        errorMessage = '任务已完成结算，无法再次进行结算';
                        break;
                    case 'E_QUEST_SUSPEND_FOUND':
                        errorMessage = '任务已设置暂停自动结算标志，无法执行结算';
                        break;
                    case 'E_QUEST_NOT_ENDED':
                        errorMessage = '任务尚未标记为结束，请先执行强制结束操作';
                        break;
                    case 'E_NO_APPROVED_USER_QUESTS':
                        errorMessage = '任务没有已批准的用户任务，无法进行结算';
                        break;
                    default:
                        errorMessage = err.response.data.message || errorMessage;
                }
            } else if (err.message) {
                errorMessage = err.message;
            }

            setSettlementError(errorMessage);
            setIsSettling(false);
        }
    };

    // 优化任务状态显示逻辑
    const getQuestStatusInfo = (questInfo) => {
        // 使用函数库中的方法判断任务状态
        const isActive = !isQuestOver(questInfo);
        const isPaid = isQuestPaid(questInfo);
        const isAutoQuest = isAutoStreamQuest(questInfo);

        let statusText = "活跃";
        let statusClass = classes.activeStatus;
        let showWarningIcon = false;
        let warningTooltip = "";

        if (isActive) {
            // 任务活跃中的状态
            statusText = "活跃";
            statusClass = classes.activeStatus;
        } else {
            // 任务已结束的状态处理
            // 1. 先检查支付状态，因为支付状态优先级最高
            if (isPaid) {
                statusText = "已支付";
                statusClass = classes.paidStatus;

                // 如果是自动任务但未设置自动结算，显示警告
                if (isAutoQuest && !questInfo.questAutoCheckout) {
                    statusText = "已支付(手动结算)";
                    statusClass = classes.warningStatus;
                    showWarningIcon = true;
                    warningTooltip = "任务已支付但未使用自动结算功能";
                }
            }
            // 2. 处理结束但未支付的情况
            else {
                statusText = "已结束";
                statusClass = classes.inactiveStatus;

                // 如果是自动任务但未设置自动结束，显示警告
                if (isAutoQuest && !questInfo.questAutoEnd) {
                    statusText = "已结束(手动结束)";
                    statusClass = classes.warningStatus;
                    showWarningIcon = true;
                    warningTooltip = "任务已结束但未设置自动结束功能";
                }
            }
        }

        return {
            statusText,
            statusClass,
            showWarningIcon,
            warningTooltip
        };
    };

    // 添加到 QuestDataReset.js 中的适当位置，例如 QuestInfoDisplay 组件后面

    // 用户任务表格组件
    const UserQuestsTable = ({ userQuests }) => {
        const classes = useStyles();

        if (!userQuests || userQuests.length === 0) {
            return (
                <Box mt={2} p={3} bgcolor="#252530" borderRadius={1} textAlign="center">
                    <Typography variant="body1" color="textSecondary">
                        暂无用户参与数据
                    </Typography>
                </Box>
            );
        }

        // 获取用户状态的标签和样式
        const getUserStatusInfo = (status) => {
            switch (status) {
                case 'approved':
                    return { label: '正常', chipClass: classes.activeStatus };
                case 'payFinish':
                    return { label: '已结算', chipClass: classes.paidStatus };
                case 'declined':
                    return { label: '被拒绝', chipClass: classes.inactiveStatus };
                case 'leftQuest':
                    return { label: '已退出', chipClass: classes.warningStatus };
                case 'pending':
                    return { label: '待审核', chipClass: classes.pendingStatus };
                default:
                    return { label: status || '未知状态', chipClass: '' };
            }
        };

        return (
            <Box mt={3}>
                <Typography variant="subtitle1" gutterBottom style={{ color: '#4caf50', fontWeight: 500 }}>
                    <GroupIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    参与用户列表 ({userQuests.length})
                </Typography>

                <TableContainer component={Paper} className={classes.tableContainer}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableHeaderCell width="150px">用户名</TableHeaderCell>
                                <TableHeaderCell width="150px">Twitch 用户名</TableHeaderCell>
                                <TableHeaderCell width="120px" align="right">瓜分金额($)</TableHeaderCell>
                                <TableHeaderCell width="120px" align="right">排行奖励($)</TableHeaderCell>
                                <TableHeaderCell width="100px" align="center">状态</TableHeaderCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {userQuests.map((userQuest, index) => {
                                const splitAmount = userQuest.splitAmountManual || userQuest.splitAmount || 0;
                                const leaderboardAmount = userQuest.leaderboardAmountManual || userQuest.leaderboardAmount || 0;
                                const { label, chipClass } = getUserStatusInfo(userQuest.status);
                                const username = userQuest.user?.username || '未知用户';
                                const twitchName = userQuest.user?.connectedAccounts?.twitch?.username || '-';
                                // const liveStreamingHours = ((userQuest.liveStreamingTime || 0) / 60).toFixed(1);
                                // const viewingHours = ((userQuest.viewingTime || 0) / 60).toFixed(1);

                                return (
                                    <TableRow key={index} className={classes.tableRow}>
                                        <TableCell className={classes.tableCell}>{username}</TableCell>
                                        <TableCell className={classes.tableCell}>
                                            <Box display="flex" alignItems="center">
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="#9146FF" style={{ minWidth: 16, marginRight: 8 }}>
                                                    <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" />
                                                </svg>
                                                {twitchName}
                                            </Box>
                                        </TableCell>
                                        <TableCell className={classes.tableCell} align="right">
                                            <Typography variant="body2" style={{ fontWeight: 500, color: '#4caf50' }}>
                                                {splitAmount.toFixed(2)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell className={classes.tableCell} align="right">
                                            <Typography variant="body2" style={{ fontWeight: 500, color: '#ff9800' }}>
                                                {leaderboardAmount.toFixed(2)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell className={classes.tableCell} align="center">
                                            <Chip
                                                label={label}
                                                size="small"
                                                className={`${classes.statusChip} ${chipClass}`}
                                            />
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    // 任务信息展示组件
    const QuestInfoDisplay = ({ questInfo }) => {
        if (!questInfo) return null;

        const isAutoQuest = isAutoStreamQuest(questInfo);
        const { statusText, statusClass, showWarningIcon, warningTooltip } = getQuestStatusInfo(questInfo);

        return (
            <Card className={classes.questDetails} elevation={3}>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>
                                    <InfoOutlinedIcon /> 任务标题:
                                </div>
                                <div className={classes.questDetailValue}>{questInfo.title}</div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>任务状态:</div>
                                <div className={classes.questDetailValue}>
                                    <span className={`${classes.statusChip} ${statusClass}`}>
                                        {statusText}
                                    </span>
                                    {showWarningIcon && (
                                        <Tooltip title={warningTooltip} placement="top" arrow>
                                            <WarningIcon className={classes.warningIcon} />
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>游戏名称:</div>
                                <div className={classes.questDetailValue}>
                                    {questInfo.game?.name || '未知游戏'}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>任务类型:</div>
                                <div className={classes.questDetailValue}>
                                    {questInfo.type || '未知类型'}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>自动化配置:</div>
                                <div className={classes.questDetailValue}>
                                    <span className={classes.configItem}>
                                        {questInfo.questAutoEnd ? '自动结束✓' : '自动结束✗'}
                                    </span>
                                    <span className={classes.configItem}>
                                        {questInfo.questAutoCheckout ? '自动结算✓' : '自动结算✗'}
                                    </span>
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>
                                    <AccessTimeIcon /> 开始时间:
                                </div>
                                <div className={classes.questDetailValue}>
                                    {getLocalTimeString(questInfo.startDateTime, 'YYYY-MM-DD HH:mm')}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>
                                    <AccessTimeIcon /> 结束时间:
                                </div>
                                <div className={classes.questDetailValue}>
                                    {getLocalTimeString(questInfo.endDateTime, 'YYYY-MM-DD HH:mm')}
                                </div>
                            </div>
                        </Grid>
                        <Grid item xs={12}>
                            <div className={classes.questDetailItem}>
                                <div className={classes.questDetailLabel}>
                                    <GroupIcon /> 参与人数:
                                </div>
                                <div className={classes.questDetailValue}>
                                    {questInfo.participantsCount || 0} 人
                                </div>
                            </div>
                        </Grid>
                    </Grid>

                    {!isAutoQuest && (
                        <Box mt={2} className={classes.warningNote}>
                            <InfoIcon className={classes.infoIcon} />
                            <Typography variant="body2">
                                注意：仅自动直播任务支持重新结算和自动入账功能
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    const handleKeyPress = (e, fetchFunction) => {
        if (e.key === 'Enter') {
            fetchFunction();
        }
    };

    return (
        <div className="quest-data-reset-container">
            {/* 已移除更新昨日排行榜区块 */}

            {/* 1. 重新结算任务数据区块 */}
            <Paper className={classes.container} elevation={3}>
                <Typography variant="h6" className={classes.title}>
                    <CalculateIcon className={classes.sectionIcon} />
                    重新结算任务奖励数据
                </Typography>
                <Typography variant="body2" className={classes.description}>
                    <InfoOutlinedIcon />
                    对指定任务的数据进行重新结算，重新计算每位用户的奖励分配，不影响用户余额。
                </Typography>
                <Box className={classes.formContainer}>
                    {/* 添加查询功能 */}
                    <div className={classes.searchContainer}>
                        <TextField
                            label="任务ID"
                            variant="outlined"
                            value={recalculateQuestId}
                            onChange={handleRecalculateQuestIdChange}
                            onKeyPress={(e) => handleKeyPress(e, fetchRecalculateQuest)}
                            className={classes.inputField}
                            placeholder="输入需要重新结算的任务ID"
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={fetchRecalculateQuest}
                            disabled={isLoadingRecalculateQuest || !recalculateQuestId}
                            className={classes.searchButton}
                            startIcon={isLoadingRecalculateQuest ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                        >
                            {isLoadingRecalculateQuest ? '查询中...' : '查询'}
                        </Button>
                    </div>

                    {/* 显示任务详情 */}
                    {recalculateQuestInfo && <QuestInfoDisplay questInfo={recalculateQuestInfo} />}

                    {/* 添加用户任务表格 */}
                    {recalculateUserQuests && recalculateUserQuests.length > 0 && (
                        <UserQuestsTable userQuests={recalculateUserQuests} />
                    )}

                    <Tooltip title="重新计算任务中所有用户的奖励分配" placement="top" arrow>
                        <span style={{ alignSelf: isMobile ? 'stretch' : 'flex-start' }}>
                            <Button
                                variant="contained"
                                onClick={() => handleOpenConfirmDialog('recalculate')}
                                disabled={isRecalculating || !recalculateQuestId || !recalculateQuestInfo || !isAutoStreamQuest(recalculateQuestInfo)}
                                className={classes.calculateButton}
                                startIcon={isRecalculating ? <CircularProgress size={20} color="inherit" /> : <CalculateIcon />}
                                fullWidth={isMobile}
                            >
                                {isRecalculating ? '结算中...' : '重新结算奖励'}
                            </Button>
                        </span>
                    </Tooltip>

                    {recalculateError && (
                        <Box className={classes.errorMessage}>
                            <ErrorIcon />
                            <span>{recalculateError}</span>
                        </Box>
                    )}

                    {recalculateSuccess && (
                        <Box className={classes.successMessage}>
                            <CheckCircleIcon />
                            <span>{recalculateSuccess}</span>
                        </Box>
                    )}
                </Box>
            </Paper>

            <Divider className={classes.divider} />

            {/* 2. 自动结算入账区块 */}
            <Paper className={classes.container} elevation={3}>
                <Typography variant="h6" className={classes.title}>
                    <AccountBalanceWalletIcon className={classes.sectionIcon} />
                    任务奖励自动结算入账
                </Typography>
                <Typography variant="body2" className={classes.description}>
                    <InfoOutlinedIcon />
                    将指定任务的奖励自动结算到用户账户，此操作会直接影响用户余额。
                </Typography>
                <Box className={classes.formContainer}>
                    {/* 添加查询功能 */}
                    <div className={classes.searchContainer}>
                        <TextField
                            label="任务ID"
                            variant="outlined"
                            value={settlementQuestId}
                            onChange={handleSettlementQuestIdChange}
                            onKeyPress={(e) => handleKeyPress(e, fetchSettlementQuest)}
                            className={classes.inputField}
                            placeholder="输入需要自动结算的任务ID"
                            fullWidth
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={fetchSettlementQuest}
                            disabled={isLoadingSettlementQuest || !settlementQuestId}
                            className={classes.searchButton}
                            startIcon={isLoadingSettlementQuest ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                        >
                            {isLoadingSettlementQuest ? '查询中...' : '查询'}
                        </Button>
                    </div>

                    {/* 显示任务详情 */}
                    {settlementQuestInfo && <QuestInfoDisplay questInfo={settlementQuestInfo} />}

                    {/* 添加用户任务表格 */}
                    {settlementUserQuests && settlementUserQuests.length > 0 && (
                        <UserQuestsTable userQuests={settlementUserQuests} />
                    )}

                    <Tooltip title="将任务奖励自动结算到用户账户" placement="top" arrow>
                        <span style={{ alignSelf: isMobile ? 'stretch' : 'flex-start' }}>
                            <Button
                                variant="contained"
                                onClick={() => handleOpenConfirmDialog('settlement')}
                                disabled={isSettling || !settlementQuestId || !settlementQuestInfo || !isAutoStreamQuest(settlementQuestInfo)}
                                className={classes.settlementButton}
                                startIcon={isSettling ? <CircularProgress size={20} color="inherit" /> : <AccountBalanceWalletIcon />}
                                fullWidth={isMobile}
                            >
                                {isSettling ? '结算中...' : '自动结算入账'}
                            </Button>
                        </span>
                    </Tooltip>

                    {settlementError && (
                        <Box className={classes.errorMessage}>
                            <ErrorIcon />
                            <span>{settlementError}</span>
                        </Box>
                    )}

                    {settlementSuccess && (
                        <Box className={classes.successMessage}>
                            <CheckCircleIcon />
                            <span dangerouslySetInnerHTML={{ __html: settlementSuccess }}></span>
                        </Box>
                    )}
                </Box>
            </Paper>

            {/* 通用确认对话框 */}
            <Dialog
                open={openConfirmDialog}
                onClose={handleCloseConfirmDialog}
                maxWidth="sm"
                PaperProps={{
                    style: {
                        backgroundColor: '#1e1e28',
                        borderRadius: 8,
                    },
                }}
            >
                <DialogTitle className={classes.dialogTitle}>
                    {confirmTitle}
                </DialogTitle>
                <DialogContent className={classes.dialogContent}>
                    <DialogContentText style={{ color: '#ddd' }}>
                        <div dangerouslySetInnerHTML={{ __html: confirmContent }} />
                    </DialogContentText>
                    {/* 添加原因输入框，仅在结算任务时显示 */}
                    {confirmActionType === 'settlement' && (
                        <TextField
                            autoFocus
                            margin="dense"
                            id="settlement-reason"
                            label="结算原因"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={settlementReason}
                            onChange={(e) => setSettlementReason(e.target.value)}
                            placeholder="请输入任务结算的原因（可选）"
                            InputLabelProps={{
                                style: { color: '#aaa' }
                            }}
                            InputProps={{
                                style: {
                                    color: '#fff',
                                    backgroundColor: '#252530',
                                    marginTop: '16px'
                                }
                            }}
                        />
                    )}

                    {/* 添加原因输入框，仅在强制结束任务时显示 */}
                    {confirmActionType === 'recalculate' && (
                        <TextField
                            autoFocus
                            margin="dense"
                            id="task-end-reason"
                            label="结束原因"
                            type="text"
                            fullWidth
                            variant="outlined"
                            value={endTaskReason}
                            onChange={(e) => setEndTaskReason(e.target.value)}
                            placeholder="请输入任务强制结束的原因"
                            InputLabelProps={{
                                style: { color: '#aaa' }
                            }}
                            InputProps={{
                                style: {
                                    color: '#fff',
                                    backgroundColor: '#252530',
                                    marginTop: '16px'
                                }
                            }}
                        />
                    )}
                </DialogContent>
                <DialogActions className={classes.dialogActions}>
                    <Button onClick={handleCloseConfirmDialog} className={classes.cancelButton}>
                        取消
                    </Button>
                    <Button
                        onClick={() => {
                            // 根据操作类型直接调用相应的函数
                            if (confirmActionType === 'recalculate') {
                                handleRecalculateRewards();
                            } else if (confirmActionType === 'settlement') {
                                handleSettleRewards();
                            } else {
                                console.error('未知的操作类型:', confirmActionType);
                            }
                        }}
                        className={classes.confirmButton}
                        autoFocus
                        // 如果是强制结束任务且没有填写原因，则禁用按钮
                        disabled={confirmActionType === 'recalculate' && !endTaskReason.trim()}>
                        确认操作
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default QuestDataReset;
